import type { ApiEffect } from '../index.h'
import type { MediaUrl } from '../interfaces/generic'
import type { PlayerUrlError } from '../interfaces/player'

import { Cancelled, is, Logic, Transport } from '@bldsoft/cappie/rest'
import { attach, createEffect, createEvent, createStore } from 'effector'
import { getCatchupUrlFx } from './catchups'
import { getLiveEventUrlByIdFx } from './live-events'
import { getTvChannelUrlAbortableFx } from './tv'
import {
  getEpisodeUrlWithoutSeasonFx,
  getTvShowEpisodeUrlFx,
  getTvShowTrailerUrlFx,
} from './tvshows'
import { getTrailerUrlFx, getUrlFx } from './vods'

export const loadVodUrl = attach({
  effect: getUrlFx,
})

export const loadChannelUrlFx = attach({
  effect: getTvChannelUrlAbortableFx,
})

export const loadEpisodeUrlPlayer: ApiEffect<
  { tvShowId: number; seasonId?: number; episodeId: number },
  MediaUrl
> = createEffect()

loadEpisodeUrlPlayer.use(async (params) => {
  if (params.seasonId === undefined || params.seasonId === null) {
    // no seasons case
    return await getEpisodeUrlWithoutSeasonFx(params)
  }
  // @ts-ignore
  return await getTvShowEpisodeUrlFx(params)
})

// TODO names below missing 'Fx'

export const loadShowTrailerUrl = attach({
  effect: getTvShowTrailerUrlFx,
})

export const loadVodTrailerUrl = attach({
  effect: getTrailerUrlFx,
})

export const loadLiveEventUrlFx = attach({
  effect: getLiveEventUrlByIdFx,
})

export const loadCatchupUrlFx = attach({ effect: getCatchupUrlFx })

// *
// * STORES
// *

// TODO each effect could have its own store, but current flow allows - one generic store

export const $currentPlayerPageUrl = createStore<MediaUrl | null>(null)
  .on(
    [
      loadVodUrl.doneData,
      loadChannelUrlFx.doneData,
      loadEpisodeUrlPlayer.doneData,
      loadShowTrailerUrl.doneData,
      loadVodTrailerUrl.doneData,
      loadLiveEventUrlFx.doneData,
      loadCatchupUrlFx.doneData,
    ],
    (_, urlObject) => urlObject
  )
  .reset([
    loadVodUrl,
    loadChannelUrlFx,
    loadEpisodeUrlPlayer /* Store reset may be later than you expect */,
    loadShowTrailerUrl,
    loadVodTrailerUrl,
    loadLiveEventUrlFx,
    loadCatchupUrlFx,
  ])
export const resetCurrentPlayerPageUrlError = createEvent()
export const $currentPlayerPageUrlError = createStore<PlayerUrlError | null>(
  null
)
  .on(
    [
      getUrlFx.fail,
      getTvChannelUrlAbortableFx.fail,
      getEpisodeUrlWithoutSeasonFx.fail,
      getTvShowEpisodeUrlFx.fail,
      getTvShowTrailerUrlFx.fail,
      getTrailerUrlFx.fail,
      getLiveEventUrlByIdFx.fail,
      getCatchupUrlFx.fail,
    ],
    (_, { error }) => {
      if (is(error, Cancelled)) {
        return { code: -1001, message: 'Request cancelled' }
      }

      if (is(error, Transport) && error.kind === 'http-error') {
        return {
          code: error.http.status,
          message: error.http.statusText,
          errorCode: '',
        }
      }

      if (is(error, Logic) && error.http.kind === 'error') {
        return {
          code: error.http.status,
          message: error.http.statusText,
          errorCode: error.errorCode ?? '',
        }
      }

      if (is(error, Transport) && error.kind === 'timeout') {
        return { code: -1002, message: 'Request timeout' }
      }

      if (is(error, Transport)) {
        return { code: -1003, message: 'Request failed' }
      }

      return { code: -1000, message: '' }
    }
  )
  .reset([
    getUrlFx,
    getTvChannelUrlAbortableFx,
    getEpisodeUrlWithoutSeasonFx,
    getTvShowEpisodeUrlFx,
    getTvShowTrailerUrlFx,
    getTrailerUrlFx,
    getLiveEventUrlByIdFx,
    getCatchupUrlFx,
    resetCurrentPlayerPageUrlError,
  ])
